import { usgsGetAllValuesForTimeSeriesId } from 'features/integrations/features/ConnectedDevices/utils/usgsGeneralUtils';
import { Card } from 'react-bootstrap';
import {
  qualifierHandler,
  roundToHundredths,
  USGSMeasureNormalizer,
} from '../utils/usgsDataSheetUtils';

const USGSDataCard = ({ timeSeries, device, pageIndex = 0 }) => {
  if (!timeSeries || !device || !device.data) return null;

  //Get all values for this time series, then pick the one at the current page index (or default to 0 if out of bounds)
  const timeSeriesID = String(timeSeries.id);
  const valuesForSeries = usgsGetAllValuesForTimeSeriesId(device, timeSeriesID);
  if (!Array.isArray(valuesForSeries) || valuesForSeries.length === 0) return null;
  const chosenRecord = valuesForSeries[pageIndex] || valuesForSeries[0];

  const normalized = USGSMeasureNormalizer(chosenRecord.value, timeSeries.unit_of_measure);
  const displayValue = roundToHundredths(chosenRecord.value);
  const displayUnit = normalized?.unit ?? timeSeries.unit_of_measure;
  const altUnit = normalized?.alt_unit ?? timeSeries.alt_unit_of_measure;
  const altValue = normalized?.alt_value ?? chosenRecord.alt_value;

  // Setup display record with normalized values and units for easier rendering below.
  // Log this to see everything that should be displayed
  const displayRecord = {
    displayValue: displayValue ?? chosenRecord.value ?? null,
    displayUnit: displayUnit ?? timeSeries.unit_of_measure,
    altValue: altValue ?? null,
    altUnit: altUnit ?? null,
    qualifier: chosenRecord.qualifier,
    approval_status: chosenRecord.approval_status,
    timestampMs:
      typeof chosenRecord.ts === 'number' ? chosenRecord.ts : new Date(chosenRecord.ts).getTime(),
    did: chosenRecord.did,
    uid: chosenRecord.uid,
    time_series_id: timeSeriesID,
    parameter_name: timeSeries.parameter_name,
    sublocation_identifier: timeSeries.sublocation_identifier,
    monitoring_location_id: timeSeries.monitoring_location_id || device.name,
    monitoring_location_name: device.monitoring_location_name,
    location: device.location,
    site_type: device.site_type,
    hydrologic_unit_code: timeSeries.hydrologic_unit_code || device.hydrologic_unit_code,
  };

  // qualifier handling may override display value and suppress unit display
  let showUnit = true;
  let renderedValue = displayValue;
  if (displayRecord?.qualifier != null && Array.isArray(displayRecord.qualifier)) {
    let qualifierValue = qualifierHandler(displayRecord.qualifier);
    // This is a safety check in case the flag was not accounted for yet. Only triggers if the value is also null.
    if (qualifierValue === false) {
      if (displayRecord?.displayValue == null) {
        qualifierValue = <strong>Data Unavailable</strong>;
      }
    }
    if (qualifierValue) {
      renderedValue = qualifierValue;
      showUnit = false;
    }
  }

  return (
    <Card className="mb-3 bg-transparent border-secondary">
      {/* Card Header */}
      <Card.Header className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center text-white">
        <div className="mb-2 mb-xl-0 d-flex flex-column">
          <div>
            <div>
              <strong>{displayRecord.parameter_name}</strong>
            </div>
            <div>
              <small>Monitoring Location: {displayRecord.monitoring_location_id}</small>
            </div>
          </div>
          {displayRecord?.sublocation_identifier ? (
            <div className="align-content-end justify-content-end d-flex">
              <small>{displayRecord.sublocation_identifier}</small>
            </div>
          ) : null}
        </div>
      </Card.Header>

      <Card.Body className="text-white">
        <div>
          {/* Station Name */}
          <div>Station Name: {displayRecord.monitoring_location_name}</div>
          <div className="mb-2">
            {/* Parameter Name and Value */}
            {/* Units are hidden if showUnit is false (e.g., when a qualifier overrides the value) */}
            <strong>{displayRecord.parameter_name}</strong>: {renderedValue ?? 'N/A'}{' '}
            {showUnit ? <span>{displayRecord.displayUnit || 'N/A'}</span> : null}
            {displayRecord.altValue != null && displayRecord.altUnit ? (
              <>
                {' '}
                / {displayRecord.altValue} {showUnit ? <span>{displayRecord.altUnit}</span> : null}
              </>
            ) : null}
          </div>

          {/* Qualifiers/Flags */}
          <div>Qualifiers/Flags: {displayRecord?.qualifier ?? 'N/A'}</div>

          {/* GPS Location */}
          <div>
            GPS Location: {displayRecord?.location?.lat}, {displayRecord?.location?.lng}
          </div>

          {/* Approval Status */}
          <div>Approval Status: {displayRecord?.approval_status ?? 'N/A'}</div>

          {/* Hydrologic Unit Code (HUC) */}
          <div>Hydrologic Unit Code (HUC): {displayRecord?.hydrologic_unit_code ?? 'N/A'}</div>

          {/* Site Type */}
          <div>Site Type: {displayRecord?.site_type ?? 'N/A'}</div>

          {/* Timestamp */}
          <div>
            Timestamp:{' '}
            {Number.isFinite(displayRecord?.timestampMs)
              ? new Date(displayRecord.timestampMs).toLocaleString(undefined, {
                  timeZoneName: 'short',
                })
              : 'N/A'}
          </div>

          {/* Time Series ID */}
          <div>
            Timeseries: <small>{displayRecord.time_series_id}</small>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default USGSDataCard;
